import { useRef, type KeyboardEvent } from 'react'
import { cx } from '../lib/cx'
import { Icon, type IconName } from './Icon'
import { tabId, tabPanelId } from './tabIds'
import './Tabs.css'

export interface TabItem<T extends string> {
  id: T
  label: string
  icon?: IconName
  disabled?: boolean
}

export interface TabsProps<T extends string> {
  /** Names the tab list for screen readers. */
  label: string
  /** Prefix for element ids; panels use tabPanelId(idPrefix, tab). */
  idPrefix: string
  tabs: readonly TabItem<T>[]
  selected: T
  onSelect: (id: T) => void
  className?: string
}


/**
 * A segmented tab list (WAI-ARIA tabs): arrow keys move between tabs, Home and End
 * jump to the ends. The parent renders each panel with role="tabpanel",
 * id={tabPanelId(prefix, id)} and aria-labelledby={tabId(prefix, id)}.
 */
export function Tabs<T extends string>({ label, idPrefix, tabs, selected, onSelect, className }: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null)
  const enabled = tabs.filter((tab) => !tab.disabled)

  const focusTab = (id: T) => {
    onSelect(id)
    listRef.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(tabId(idPrefix, id))}`)?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = enabled.findIndex((tab) => tab.id === selected)
    let next: TabItem<T> | undefined
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = enabled[(index + 1) % enabled.length]
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = enabled[(index - 1 + enabled.length) % enabled.length]
    else if (event.key === 'Home') next = enabled[0]
    else if (event.key === 'End') next = enabled[enabled.length - 1]
    if (next) {
      event.preventDefault()
      focusTab(next.id)
    }
  }

  return (
    <div ref={listRef} className={cx('tabs', className)} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {tabs.map((tab) => {
        const isSelected = tab.id === selected
        return (
          <button
            key={tab.id}
            id={tabId(idPrefix, tab.id)}
            type="button"
            role="tab"
            className={cx('tabs__tab', isSelected && 'tabs__tab--selected')}
            aria-selected={isSelected}
            aria-controls={tabPanelId(idPrefix, tab.id)}
            aria-disabled={tab.disabled || undefined}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => {
              if (!tab.disabled) onSelect(tab.id)
            }}
          >
            {tab.icon && <Icon name={tab.icon} size={20} />}
            <span>{tab.label}</span>
          </button>
        )
      })}
    </div>
  )
}
